import React, { createContext, useContext, useEffect, useState } from "react";
import { AVATAR_POSITION } from "../helper/enums";
import { TEMPLATE_IDS } from "../helper/helper";
import { models } from "../helper/models";
import type { ChatType, ScriptType } from "../helper/types";

const defaultScript: ScriptType = {
  id: new Date().getTime(),
  question: "",
  answer: "",
  timestamp: "",
  userEndTimestamp: "",
  userStartTimestamp: "",
};

interface AvatarModelContextProps {
  init: boolean;
  setInit: React.Dispatch<React.SetStateAction<boolean>>;
  isGeneratingResponse: boolean;
  setIsGeneratingResponse: React.Dispatch<React.SetStateAction<boolean>>;
  isAvatarLoaded: boolean;
  setIsAvatarLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  chats: ChatType[];
  setChats: React.Dispatch<React.SetStateAction<ChatType[]>>;
  handleAddChat: (newChat: ChatType) => void;
  lipSyncUrl: any;
  setLipSyncUrl: React.Dispatch<React.SetStateAction<any>>;
  audioUrl: string;
  setAudioUrl: React.Dispatch<React.SetStateAction<string>>;
  bgImage: string;
  setBgImage: React.Dispatch<React.SetStateAction<string>>;
  avtar: string;
  setAvtar: React.Dispatch<React.SetStateAction<string>>;
  animation: string;
  setAnimation: React.Dispatch<React.SetStateAction<string>>;
  avatarPosition: any;
  setAvatarPosition: React.Dispatch<React.SetStateAction<any>>;
  currentScript: any;
  setCurrentScript: React.Dispatch<React.SetStateAction<any>>;
  scripts: ScriptType[];
  setScripts: React.Dispatch<React.SetStateAction<ScriptType[]>>;
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  updateLastChatText: (newText: string) => void;
  setEnableVr: React.Dispatch<React.SetStateAction<boolean>>; //! to be removed
  enableVr: boolean; //! to be removed
  expandedAvatar: boolean; //! to be removed
  setExpandedAvatar: React.Dispatch<React.SetStateAction<boolean>>; //! to be removed
  demoWidgetSize: string;
  setDemoWidgetSize: React.Dispatch<React.SetStateAction<"small" | "large">>;
  setPendingResponses: React.Dispatch<React.SetStateAction<any>>;
  pendingResponses: any;
  selectedSpeaker: string;
  setSelectedSpeaker: React.Dispatch<React.SetStateAction<string>>;
  clearContext: () => void;
  skipped: boolean;
  setSkipped: any;
}

const AvatarModelContext = createContext<AvatarModelContextProps | null>(null);

const AvatarModelContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [init, setInit] = useState<boolean>(false);
  const [isGeneratingResponse, setIsGeneratingResponse] =
    useState<boolean>(false);
  const [isAvatarLoaded, setIsAvatarLoaded] = useState<boolean>(false);
  const [chats, setChats] = useState<ChatType[]>([]);
  const [lipSyncUrl, setLipSyncUrl] = useState<any>({ mouthCues: [] });
  // const [audioUrl, setAudioUrl] = useState<string>(EMPTY_AUDIO);
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [bgImage, setBgImage] = useState<string>("/textures/roleplay.png");
  const [avtar, setAvtar] = useState<string>(models[0].url);
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>("");
  const [animation, setAnimation] = useState<any>("");
  const [avatarPosition, setAvatarPosition] = useState<any>(
    AVATAR_POSITION.LEFT
  );
  const [currentScript, setCurrentScript] = useState<any>({});
  const [enableVr, setEnableVr] = useState(false);
  const [expandedAvatar, setExpandedAvatar] = useState(false);
  const [demoWidgetSize, setDemoWidgetSize] = useState<"small" | "large">(
    "small"
  );
  const [pendingResponses, setPendingResponses] = useState(null);
  const [skipped, setSkipped] = useState(false);

  const [scripts, setScripts] = useState<ScriptType[]>([defaultScript]);
  const [formData, setFormData] = useState<any>({
    templateId: TEMPLATE_IDS["doctor-1"],
  });

  const handleAddChat = (newChat: ChatType) => {
    setChats((prev) => [...prev, newChat]);
  };

  const updateLastChatText = (newText: string) => {
    setChats((prevChats) => {
      if (prevChats.length === 0) return prevChats;
      const updatedChats = [...prevChats];
      const index =
        (updatedChats.length - 1) % 2 === 0
          ? updatedChats.length - 1
          : updatedChats.length - 2;
      updatedChats[index].text += newText;
      return updatedChats;
    });
  };

  const clearContext = () => {
    setInit(false);
    setIsGeneratingResponse(false);
    setIsAvatarLoaded(false);
    setChats([]);
    setLipSyncUrl({ mouthCues: [] });
    // const [audioUrl, setAudioUrl] = useState<string>(EMPTY_AUDIO);
    setAudioUrl("");
    setBgImage("/textures/roleplay.png");
    setAvtar(models[0].url);
    setSelectedSpeaker("");
    setAnimation(null);
    setAvatarPosition(AVATAR_POSITION.LEFT);
    setCurrentScript({});
    setEnableVr(false);
    setPendingResponses(null);

    setScripts([defaultScript]);
    setFormData({
      templateId: TEMPLATE_IDS["doctor-1"],
    });
  };

  useEffect(() => {
    return () => {
      setAudioUrl("");
      setLipSyncUrl({ mouthCues: [] });
      setScripts([defaultScript]);
    };
  }, []);

  const AvatarModelContextValue: AvatarModelContextProps = {
    init,
    setInit,
    isGeneratingResponse,
    setIsGeneratingResponse,
    isAvatarLoaded,
    setIsAvatarLoaded,
    chats,
    setChats,
    handleAddChat,
    lipSyncUrl,
    setLipSyncUrl,
    audioUrl,
    setAudioUrl,
    bgImage,
    setBgImage,
    avtar,
    setAvtar,
    animation,
    setAnimation,
    avatarPosition,
    setAvatarPosition,
    currentScript,
    setCurrentScript,
    scripts,
    setScripts,
    formData,
    setFormData,
    updateLastChatText,
    enableVr,
    expandedAvatar,
    setExpandedAvatar,
    setEnableVr,
    setPendingResponses,
    pendingResponses,
    selectedSpeaker,
    setSelectedSpeaker,
    clearContext,
    demoWidgetSize,
    setDemoWidgetSize,
    setSkipped,
    skipped,
  };

  return (
    <AvatarModelContext.Provider value={AvatarModelContextValue}>
      {children}
    </AvatarModelContext.Provider>
  );
};

export const useAvatarModelContext = () => {
  return useContext(AvatarModelContext) as AvatarModelContextProps;
};

export default AvatarModelContextProvider;
